import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "popup", "list", "backdrop", "header"]
  static values = { participants: Array, referrals: Array }

  connect() {
    this.activeIndex = 0
    this.mentioning = false
    this.mentionStart = -1
    this.mentionTrigger = null // "@" or "#"
    this.filtered = []
    this.updateBackdrop()

    this._syncScroll = () => {
      if (this.hasBackdropTarget) {
        this.backdropTarget.scrollTop = this.inputTarget.scrollTop
      }
    }
    this.inputTarget.addEventListener("scroll", this._syncScroll)

    // Prevent popup interactions from blurring the textarea (keeps keyboard open on mobile)
    this._preventBlur = (e) => e.preventDefault()
    this.popupTarget.addEventListener("mousedown", this._preventBlur)

    // Track touch movement to distinguish taps from scrolls
    this._touchStartY = 0
    this._isTouchScrolling = false
    this._onTouchStart = (e) => {
      this._touchStartY = e.touches[0].clientY
      this._isTouchScrolling = false
    }
    this._onTouchMove = (e) => {
      if (Math.abs(e.touches[0].clientY - this._touchStartY) > 5) {
        this._isTouchScrolling = true
      }
    }
    this.popupTarget.addEventListener("touchstart", this._onTouchStart)
    this.popupTarget.addEventListener("touchmove", this._onTouchMove)
  }

  disconnect() {
    this.inputTarget.removeEventListener("scroll", this._syncScroll)
    this.popupTarget.removeEventListener("mousedown", this._preventBlur)
    this.popupTarget.removeEventListener("touchstart", this._onTouchStart)
    this.popupTarget.removeEventListener("touchmove", this._onTouchMove)
  }

  onInput() {
    this.updateBackdrop()

    const text = this.inputTarget.value
    const cursorPos = this.inputTarget.selectionStart
    const textBeforeCursor = text.slice(0, cursorPos)

    // Check for both @ and # triggers — use whichever appears last
    const atIndex = textBeforeCursor.lastIndexOf("@")
    const hashIndex = textBeforeCursor.lastIndexOf("#")

    let triggerIndex = -1
    let trigger = null

    if (atIndex > hashIndex) {
      triggerIndex = atIndex
      trigger = "@"
    } else if (hashIndex > atIndex) {
      triggerIndex = hashIndex
      trigger = "#"
    } else {
      this.closeMention()
      return
    }

    // Trigger must be at start or after whitespace
    if (triggerIndex > 0 && textBeforeCursor[triggerIndex - 1] !== " " && textBeforeCursor[triggerIndex - 1] !== "\n") {
      this.closeMention()
      return
    }

    const query = textBeforeCursor.slice(triggerIndex + 1)

    if (query.includes(" ") && query.split(" ").length > 3) {
      this.closeMention()
      return
    }

    this.mentioning = true
    this.mentionStart = triggerIndex
    this.mentionTrigger = trigger

    if (trigger === "@") {
      this.filterItems(query, this.participantsValue, "Mention someone in this conversation")
    } else {
      this.filterItems(query, this.referralsValue, "Mention a referral in this conversation")
    }
  }

  onKeydown(event) {
    if (!this.mentioning) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, this.filtered.length - 1)
      this.renderList()
      this.scrollActiveIntoView()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, 0)
      this.renderList()
      this.scrollActiveIntoView()
    } else if (event.key === "Enter" && this.filtered.length > 0) {
      event.preventDefault()
      event.stopPropagation()
      this.selectItem(this.activeIndex)
    } else if (event.key === "Escape") {
      this.closeMention()
    }
  }

  filterItems(query, items, headerText) {
    const q = query.toLowerCase()
    this.filtered = items.filter(item => {
      const searchText = item.search || item.name
      return searchText.toLowerCase().includes(q)
    })
    this.activeIndex = 0

    if (this.filtered.length === 0) {
      this.closeMention()
      return
    }

    if (this.hasHeaderTarget) {
      this.headerTarget.textContent = headerText
    }
    this.renderList()
    this.popupTarget.classList.remove("d-none")
  }

  renderList() {
    if (this.mentionTrigger === "#") {
      const providerIcon = (type) => type === 'provider' ? 'fa-user' : 'fa-buildings'
      this.listTarget.innerHTML = this.filtered.map((r, i) => `
        <div class="mention-item mention-referral-item ${i === this.activeIndex ? 'mention-item-active' : ''}"
             data-action="touchend->mention#selectTouch click->mention#select"
             data-index="${i}">
          <div class="mention-referral-info">
            <div class="mention-referral-row">
              <i class="fa-regular ${r.direction === 'inbound' ? 'fa-arrow-down-to-line' : 'fa-arrow-up-from-line'} mention-referral-icon"></i>
              <span class="mention-item-name">${this.escapeHtml(r.name)}</span>
            </div>
            <div class="mention-referral-row">
              ${r.provider_avatar_url
                ? `<img src="${r.provider_avatar_url}" class="mention-referral-avatar">`
                : `<span class="mention-referral-badge"><i class="fa-regular ${providerIcon(r.provider_type)}"></i></span>`}
              <span class="mention-referral-provider-name">${this.escapeHtml(r.provider_name)}</span>
            </div>
          </div>
        </div>
      `).join("")
    } else {
      this.listTarget.innerHTML = this.filtered.map((p, i) => `
        <div class="mention-item ${i === this.activeIndex ? 'mention-item-active' : ''}"
             data-action="touchend->mention#selectTouch click->mention#select"
             data-index="${i}">
          ${p.avatar}
          <span class="mention-item-name">${this.escapeHtml(p.name)}</span>
        </div>
      `).join("")
    }
  }

  scrollActiveIntoView() {
    const active = this.listTarget.querySelector(".mention-item-active")
    if (active) active.scrollIntoView({ block: "nearest" })
  }

  select(event) {
    // Skip if already handled by pointerdown
    if (this._pointerHandled) {
      this._pointerHandled = false
      return
    }
    const index = parseInt(event.currentTarget.dataset.index)
    this.selectItem(index)
  }

  selectTouch(event) {
    if (this._isTouchScrolling) return
    event.preventDefault() // Prevent subsequent click from double-firing
    this._pointerHandled = true
    const index = parseInt(event.currentTarget.dataset.index)
    this.selectItem(index)
  }

  selectItem(index) {
    const item = this.filtered[index]
    if (!item) return

    const text = this.inputTarget.value
    const before = text.slice(0, this.mentionStart)
    const after = text.slice(this.inputTarget.selectionStart)
    const insertText = `${this.mentionTrigger}${item.name} `

    this.inputTarget.value = before + insertText + after
    this.inputTarget.selectionStart = this.inputTarget.selectionEnd = before.length + insertText.length

    this.closeMention()
    this.updateBackdrop()
    this.inputTarget.focus()
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  closeMention() {
    this.mentioning = false
    this.mentionStart = -1
    this.mentionTrigger = null
    this.filtered = []
    this.popupTarget.classList.add("d-none")
  }

  updateBackdrop() {
    if (!this.hasBackdropTarget) return

    const text = this.inputTarget.value
    let html = this.escapeHtml(text)

    // Highlight completed @mentions
    this.participantsValue.forEach(p => {
      const escapedName = this.escapeHtml(p.name)
      const regex = new RegExp(`@${this.escapeRegex(escapedName)}`, "g")
      html = html.replace(regex, `<span class="mention-highlight">@${escapedName}</span>`)
    })

    // Highlight completed #referral mentions
    this.referralsValue.forEach(r => {
      const escapedName = this.escapeHtml(r.name)
      const regex = new RegExp(`#${this.escapeRegex(escapedName)}`, "g")
      html = html.replace(regex, `<span class="mention-highlight">#${escapedName}</span>`)
    })

    // Highlight active trigger being typed
    if (this.mentioning && this.mentionStart >= 0) {
      const cursorPos = this.inputTarget.selectionStart
      const activeQuery = this.escapeHtml(text.slice(this.mentionStart, cursorPos))
      if (!html.includes(`<span class="mention-highlight">${activeQuery}</span>`)) {
        const beforeMention = html.slice(0, this.mentionStart)
        const afterMention = html.slice(this.mentionStart + activeQuery.length)
        if (html.slice(this.mentionStart, this.mentionStart + activeQuery.length) === activeQuery) {
          html = beforeMention + `<span class="mention-highlight">${activeQuery}</span>` + afterMention
        }
      }
    }

    this.backdropTarget.innerHTML = html + "\n"
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  }
}
